import { TextField, type TextFieldProps } from '@mui/material';
import { forwardRef, useCallback, useMemo } from 'react';

type DateTimeFieldProps = Omit<TextFieldProps, 'type' | 'value' | 'onChange'> & {
  type: 'date' | 'datetime-local';
  value: Date | null;
  onChange: (date: Date | null) => void;
};

const isInvalidDate = (date: Date) => Number.isNaN(date.getTime());

export const DateTimeField = forwardRef<HTMLDivElement, DateTimeFieldProps>(
  ({ type, value, onChange, ...props }, ref) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // 年を4桁に制限（1000-9999）
        if (inputValue) {
          const yearMatch = inputValue.match(/^(\d+)/);
          if (yearMatch && yearMatch[1]) {
            const year = parseInt(yearMatch[1], 10);
            // 年が4桁を超える場合は処理しない
            if (year > 9999) {
              return;
            }
          }
        }

        const date = new Date(inputValue);

        // 日付をクリアすると空文字になる
        if (isInvalidDate(date)) {
          onChange(null);
        } else {
          if (type === 'date') {
            // inputValueのYYYY-MM-DDがUTCのYYYY-MM-DD 00:00として解釈されるため、JSTのYYYY-MM-DD 00:00になるようにする
            const jstDate = new Date(Number(date) + date.getTimezoneOffset() * 60000);
            onChange(jstDate);
          } else {
            onChange(date);
          }
        }
      },
      [onChange, type],
    );

    const normalizedValue: string | undefined = useMemo(() => {
      if (!value) {
        return undefined;
      }

      // <input type="datetime-local"> はISO文字列の一部しか受け付けずタイムゾーンが考慮されないため
      // タイムゾーンの影響を打ち消して文字列が正しくJSTになるようにする
      const d = new Date(Number(value) - value.getTimezoneOffset() * 60000);

      if (type === 'date') {
        return d.toISOString().split('T')[0]; // YYYY-MM-DD
      } else {
        return d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
      }
    }, [type, value]);

    return (
      <TextField
        variant="outlined"
        {...props}
        ref={ref}
        type={type}
        value={normalizedValue ?? ''}
        onChange={handleChange}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: {
            min: type === 'date' ? '1000-01-01' : '1000-01-01T00:00',
            max: type === 'date' ? '9999-12-31' : '9999-12-31T23:59',
          },
        }}
      />
    );
  },
);
